/**
 * Accuse panel — suspect selection opened at the phone booth. Buttons emit
 * `player:interact` with a suspect InteractTarget; GAME decides the verdict.
 * Owned by UI.
 */
import type { EventBus, Vec3 } from '../core/types'
import { emitAccusation } from './accusation'
import type { CaseLog } from './caseLog'
import {
  ACCUSE_BUTTON_LABEL,
  ACCUSE_CLOSE_LABEL,
  ACCUSE_EMPTY,
  ACCUSE_SUBTITLE,
  ACCUSE_TITLE,
  HIDDEN_CLASS
} from './constants'
import { el } from './dom'

export interface AccusePanel {
  open(): void
  close(): void
  isOpen(): boolean
}

export const createAccusePanel = (
  root: HTMLElement,
  bus: EventBus,
  caseLog: CaseLog,
  getPlayerPosition: () => Vec3,
  onClose: () => void
): AccusePanel => {
  const panel = el('section', `gum-panel gum-accuse ${HIDDEN_CLASS}`)
  const suspectsList = el('ul', 'gum-list gum-accuse-list')
  const actions = el('div', 'gum-accuse-actions')
  const closeButton = el('button', 'gum-button gum-button-quiet', ACCUSE_CLOSE_LABEL)
  closeButton.type = 'button'
  actions.append(closeButton)
  panel.append(
    el('h2', 'gum-panel-title', ACCUSE_TITLE),
    el('p', 'gum-panel-sub', ACCUSE_SUBTITLE),
    suspectsList,
    actions
  )
  root.append(panel)

  let open = false

  const close = (): void => {
    if (!open) return
    open = false
    panel.classList.add(HIDDEN_CLASS)
    onClose()
  }

  closeButton.addEventListener('click', close)

  const render = (): void => {
    const suspects = caseLog.suspects()
    if (suspects.length === 0) {
      suspectsList.replaceChildren(el('li', 'gum-empty', ACCUSE_EMPTY))
      return
    }
    suspectsList.replaceChildren(
      ...suspects.map((suspect) => {
        const row = el('li', 'gum-suspect-row')
        const button = el('button', 'gum-button', ACCUSE_BUTTON_LABEL)
        button.type = 'button'
        button.addEventListener('click', () => {
          emitAccusation(bus, suspect.id, suspect.name, getPlayerPosition())
        })
        row.append(el('span', 'gum-suspect-name', suspect.name.toUpperCase()), button)
        return row
      })
    )
  }

  caseLog.subscribe(render)
  render()

  return {
    open: () => {
      if (open) return
      open = true
      panel.classList.remove(HIDDEN_CLASS)
    },
    close,
    isOpen: () => open
  }
}
